import { FC, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { IProduce } from './types'
import { getProduceVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { GET_ALL_PRODUCES } from '../../../query/produce'
import { EXPORT_PRODUCE_QUERY } from '../../../query/export-produce'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'

const Produce: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IProduce[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [specialIngredient, setSpecialIngredient] = useState<any>(getProduceVars)
  const [order, setOrder] = useState([
    {
      class: 'ASC',
      category: 'ASC',
      subcategory: 'ASC',
      functionalUnit: 'ASC',
    },
  ])

  const { data, loading, error, refetch } = useQuery(GET_ALL_PRODUCES, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getProduceVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.taxonomyProduceFunctionalUnits.pageInfo.endCursor)
    setStartCursor(data.taxonomyProduceFunctionalUnits.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/food/produce/ingredient',
      'taxonomyProduceFunctionalUnits',
      'taxonomyProduceFunctionalUnit'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].class) {
      if (!loading) {
        setSpecialIngredient({
          ...filterEnums(tableSearch, getProduceVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getProduceVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'TaxonomyProduceFunctionalUnits',
      query: EXPORT_PRODUCE_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: '/food/produce',
                title: 'Produce Table',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Produce</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={() => navigate('/food/produce/ingredient?itemId=new')}>
            Add Produce
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={specialIngredient || getProduceVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default Produce
